import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Empty,
  Input,
  Modal,
  Row,
  Spin,
  Tag,
  Typography,
  message,
  Popconfirm,
  Space,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  SendOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { ChevronLeft } from 'lucide-react';
import { useMyContext } from 'Context/MyContextProvider';
import Flex from 'components/shared-components/Flex';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import {
  getFlows,
  createFlow,
  deleteFlow,
  duplicateFlow,
  publishFlow,
  unpublishFlow,
} from 'services/chatbotFlowService';

const { Title, Text } = Typography;

const FlowList = () => {
  const { groupId, name } = useParams();
  const navigate = useNavigate();
  const { UserData, Permisson } = useMyContext();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (!Permisson?.includes('View Chatbot')) navigate('404');
  }, [Permisson, navigate]);

  const fetchFlows = async () => {
    setLoading(true);
    try {
      const res = await getFlows({
        group_id: groupId,
        search: search || undefined,
      });
      const payload = res?.data;
      const list = Array.isArray(payload) ? payload : payload?.data || [];
      setFlows(list);
    } catch {
      message.error('Failed to load flows');
      setFlows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFlows();
  }, [groupId, search]);

  const handleCreate = async () => {
    if (!newName.trim()) {
      message.warning('Enter a flow name');
      return;
    }
    try {
      const res = await createFlow({
        name: newName.trim(),
        group_id: Number(groupId),
      });
      const flow = res?.data || res;
      message.success('Flow created');
      setCreateOpen(false);
      setNewName('');
      navigate(`/chatbot-flows/${groupId}/${name}/editor/${flow.id}`);
    } catch {
      message.error('Failed to create flow');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await duplicateFlow(id);
      message.success('Flow duplicated');
      fetchFlows();
    } catch {
      message.error('Duplicate failed');
    }
  };

  const handlePublish = async (id) => {
    try {
      await publishFlow(id, '');
      message.success('Flow published');
      fetchFlows();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Publish failed — validate flow first');
    }
  };

  const handleUnpublish = async (id) => {
    try {
      await unpublishFlow(id);
      message.success('Flow unpublished');
      fetchFlows();
    } catch {
      message.error('Unpublish failed');
    }
  };

  const groupTitle = decodeURIComponent(name || 'Group');

  return (
    <Card>
      <Row justify="space-between" align="middle" gutter={ROW_GUTTER}>
        <Col>
          <Flex alignItems="center" gap={8}>
            <Button
              type="text"
              icon={<ChevronLeft size={20} />}
              onClick={() => navigate('/chatbot-flows')}
            />
            <div>
              <Title level={4} style={{ margin: 0 }}>
                Flows — {groupTitle}
              </Title>
              <Text type="secondary">Build and publish chatbot flows for this group</Text>
            </div>
          </Flex>
        </Col>
        <Col>
          <Space>
            <Input.Search
              placeholder="Search flows"
              allowClear
              onSearch={setSearch}
              style={{ width: 220 }}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              Create Flow
            </Button>
          </Space>
        </Col>
      </Row>

      {loading ? (
        <Flex justifyContent="center" style={{ padding: 48 }}>
          <Spin size="large" />
        </Flex>
      ) : flows.length === 0 ? (
        <Empty description="No flows yet" style={{ marginTop: 48 }}>
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            Create your first flow
          </Button>
        </Empty>
      ) : (
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          {flows.map((flow) => (
            <Col xs={24} sm={12} lg={8} key={flow.id}>
              <Card
                hoverable
                actions={[
                  <Link
                    key="edit"
                    to={`/chatbot-flows/${groupId}/${name}/editor/${flow.id}`}
                  >
                    <EditOutlined /> Edit
                  </Link>,
                  <span key="dup" onClick={() => handleDuplicate(flow.id)}>
                    <CopyOutlined /> Duplicate
                  </span>,
                  flow.is_active ? (
                    <span key="unpub" onClick={() => handleUnpublish(flow.id)}>
                      <StopOutlined /> Unpublish
                    </span>
                  ) : (
                    <span key="pub" onClick={() => handlePublish(flow.id)}>
                      <SendOutlined /> Publish
                    </span>
                  ),
                  <Popconfirm
                    key="del"
                    title="Delete this draft?"
                    onConfirm={async () => {
                      try {
                        await deleteFlow(flow.id);
                        message.success('Deleted');
                        fetchFlows();
                      } catch {
                        message.error('Cannot delete — unpublish first if live');
                      }
                    }}
                  >
                    <span>
                      <DeleteOutlined /> Delete
                    </span>
                  </Popconfirm>,
                ]}
              >
                <Title level={5}>{flow.name}</Title>
                <Space wrap>
                  <Tag color={flow.status === 'published' ? 'green' : 'default'}>
                    {flow.status === 'published' ? 'Published' : 'Draft'}
                  </Tag>
                  {flow.is_active && <Tag color="blue">Live</Tag>}
                  <Tag>v{flow.version}</Tag>
                </Space>
                <div style={{ marginTop: 8 }}>
                  {(flow.trigger_keywords || []).slice(0, 4).map((kw) => (
                    <Tag key={kw}>{kw}</Tag>
                  ))}
                </div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                  {flow.node_count ?? 0} nodes · Updated{' '}
                  {flow.updated_at ? new Date(flow.updated_at).toLocaleString() : '—'}
                </Text>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal
        title="Create flow"
        open={createOpen}
        onOk={handleCreate}
        onCancel={() => setCreateOpen(false)}
        okText="Create & open editor"
      >
        <Input
          placeholder="Flow name e.g. product-commerce-demo"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={handleCreate}
        />
      </Modal>
    </Card>
  );
};

export default FlowList;
